// AGC: Conflict Detection Utility
// AGC: Detects scheduling conflicts for users and machines
// AGC: Prevents overlapping task assignments

import type { Task } from '../types/task'

/**
 * AGC: Conflict detection result
 */
export interface ConflictResult {
  hasConflict: boolean
  conflictingTask?: Task
  suggestedSlot?: {
    start: Date
    end: Date
  }
}

/**
 * AGC: Resource type for conflict checking
 */
export type ResourceType = 'user' | 'machine'

/**
 * AGC: Check if two time windows overlap
 * AGC: Returns true if windows intersect
 */
export const hasTimeOverlap = (
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): boolean => {
  // AGC: Overlap if start2 is before end1 AND end2 is after start1
  return start2 < end1 && end2 > start1
}

/**
 * AGC: Parse ISO date string to Date, with fallback
 */
const parseDate = (dateStr?: string): Date | null => {
  if (!dateStr) return null
  try {
    return new Date(dateStr)
  } catch {
    return null
  }
}

/**
 * AGC: Check for resource conflicts
 * AGC: @param resourceId - User UID or Machine ID
 * AGC: @param resourceType - 'user' or 'machine'
 * AGC: @param plannedStart - Planned start time
 * AGC: @param plannedEnd - Planned end time
 * AGC: @param excludeTaskId - Current task ID to exclude when editing
 * AGC: @param allTasks - All tasks to check against
 */
export const checkResourceConflicts = (
  resourceId: string | null | undefined,
  resourceType: ResourceType,
  plannedStart: Date,
  plannedEnd: Date,
  excludeTaskId: string | undefined,
  allTasks: Task[]
): ConflictResult => {
  // AGC: No resource assigned, no conflict
  if (!resourceId) {
    return { hasConflict: false }
  }

  // AGC: Filter tasks assigned to this resource
  const assignedTasks = allTasks.filter((task) => {
    // AGC: Exclude current task when editing
    if (task.id === excludeTaskId) return false

    // AGC: Check resource assignment
    if (resourceType === 'user') {
      return task.assignedToId === resourceId
    } else {
      return task.assignedMachineId === resourceId
    }
  })

  // AGC: Check each assigned task for time overlap
  for (const task of assignedTasks) {
    const taskStart = parseDate(task.plannedStart)
    const taskEnd = parseDate(task.plannedEnd)

    // AGC: Skip tasks without time windows
    if (!taskStart || !taskEnd) continue

    // AGC: Check for overlap
    if (hasTimeOverlap(taskStart, taskEnd, plannedStart, plannedEnd)) {
      // AGC: Conflict found - calculate suggested slot after this task
      const suggestedStart = new Date(taskEnd.getTime())
      const duration = plannedEnd.getTime() - plannedStart.getTime()
      const suggestedEnd = new Date(suggestedStart.getTime() + duration)

      return {
        hasConflict: true,
        conflictingTask: task,
        suggestedSlot: {
          start: suggestedStart,
          end: suggestedEnd,
        },
      }
    }
  }

  return { hasConflict: false }
}

/**
 * AGC: Check conflicts for both user and machine assignments
 * AGC: Returns array of conflicts (one or both resources may conflict)
 */
export const checkAllResourceConflicts = (
  assignedUserId: string | null | undefined,
  assignedMachineId: string | null | undefined,
  plannedStart: Date,
  plannedEnd: Date,
  excludeTaskId: string | undefined,
  allTasks: Task[]
): { user: ConflictResult; machine: ConflictResult } => {
  const userConflict = checkResourceConflicts(
    assignedUserId,
    'user',
    plannedStart,
    plannedEnd,
    excludeTaskId,
    allTasks
  )

  const machineConflict = checkResourceConflicts(
    assignedMachineId,
    'machine',
    plannedStart,
    plannedEnd,
    excludeTaskId,
    allTasks
  )

  return { user: userConflict, machine: machineConflict }
}

/**
 * AGC: Find next available time slot for a resource
 * AGC: Scans through assigned tasks to find a free window
 * AGC: @param duration - Desired duration in milliseconds
 */
export const findNextAvailableSlot = (
  resourceId: string,
  resourceType: ResourceType,
  startFrom: Date,
  duration: number,
  allTasks: Task[]
): { start: Date; end: Date } | null => {
  // AGC: Get all tasks assigned to this resource, sorted by start time
  const assignedTasks = allTasks
    .filter((task) => {
      if (resourceType === 'user') {
        return task.assignedToId === resourceId && task.plannedStart
      } else {
        return task.assignedMachineId === resourceId && task.plannedStart
      }
    })
    .map((task) => ({
      start: parseDate(task.plannedStart),
      end: parseDate(task.plannedEnd),
    }))
    .filter((slot) => slot.start && slot.end && slot.start! > startFrom)
    .sort((a, b) => a.start!.getTime() - b.start!.getTime()) as {
    start: Date
    end: Date
  }[]

  // AGC: If no future tasks, next slot is right after startFrom
  if (assignedTasks.length === 0) {
    return {
      start: startFrom,
      end: new Date(startFrom.getTime() + duration),
    }
  }

  // AGC: Check gap between startFrom and first task
  const firstTask = assignedTasks[0]
  const gapDuration = firstTask.start.getTime() - startFrom.getTime()
  if (gapDuration >= duration) {
    return {
      start: startFrom,
      end: new Date(startFrom.getTime() + duration),
    }
  }

  // AGC: Check gaps between consecutive tasks
  for (let i = 0; i < assignedTasks.length - 1; i++) {
    const currentEnd = assignedTasks[i].end
    const nextStart = assignedTasks[i + 1].start
    const gap = nextStart.getTime() - currentEnd.getTime()

    if (gap >= duration) {
      return {
        start: currentEnd,
        end: new Date(currentEnd.getTime() + duration),
      }
    }
  }

  // AGC: No gaps found, schedule after last task
  const lastTask = assignedTasks[assignedTasks.length - 1]
  return {
    start: lastTask.end,
    end: new Date(lastTask.end.getTime() + duration),
  }
}

/**
 * AGC: Calculate resource occupancy percentage for a time period
 * AGC: @param resourceId - User UID or Machine ID
 * AGC: @param resourceType - 'user' or 'machine'
 * AGC: @param startDate - Period start
 * AGC: @param endDate - Period end
 * AGC: @param allTasks - All tasks
 * AGC: @returns Percentage (0-100)
 */
export const calculateOccupancy = (
  resourceId: string,
  resourceType: ResourceType,
  startDate: Date,
  endDate: Date,
  allTasks: Task[]
): number => {
  // AGC: Filter tasks assigned to this resource within the period
  const relevantTasks = allTasks.filter((task) => {
    const taskStart = parseDate(task.plannedStart)
    const taskEnd = parseDate(task.plannedEnd)

    if (!taskStart || !taskEnd) return false

    // AGC: Check resource assignment
    const isAssigned =
      resourceType === 'user'
        ? task.assignedToId === resourceId
        : task.assignedMachineId === resourceId

    if (!isAssigned) return false

    // AGC: Check if task overlaps with period
    return hasTimeOverlap(startDate, endDate, taskStart, taskEnd)
  })

  // AGC: Calculate total busy time
  let totalBusyTime = 0
  for (const task of relevantTasks) {
    const taskStart = parseDate(task.plannedStart)!
    const taskEnd = parseDate(task.plannedEnd)!

    // AGC: Clamp to period boundaries
    const effectiveStart = new Date(Math.max(taskStart.getTime(), startDate.getTime()))
    const effectiveEnd = new Date(Math.min(taskEnd.getTime(), endDate.getTime()))

    totalBusyTime += effectiveEnd.getTime() - effectiveStart.getTime()
  }

  // AGC: Calculate period duration
  const periodDuration = endDate.getTime() - startDate.getTime()

  // AGC: Calculate percentage
  const occupancy = (totalBusyTime / periodDuration) * 100

  return Math.min(100, Math.round(occupancy))
}

/**
 * AGC: Format conflict message for UI
 */
export const formatConflictMessage = (
  conflict: ConflictResult,
  resourceType: ResourceType,
  resourceName: string
): string => {
  if (!conflict.hasConflict || !conflict.conflictingTask) {
    return ''
  }

  const task = conflict.conflictingTask
  const resourceTypeLabel = resourceType === 'user' ? 'Operador' : 'Máquina'

  return `${resourceTypeLabel} "${resourceName}" já está alocado(a) para a tarefa "${task.title}" no mesmo horário.`
}
